import DesignTokens from '@tableau/design-tokens';
import { DestinationItem } from '@tableau/nested-project-explorer';
import { ButtonType, ButtonWidget, InlineAlertType, InlineAlertWidget, RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption, RadioLayoutType, TabStyles, TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';

import { PoolAssignmentDropdown } from '../../bridgeNetworkAllowlist/addNewDomainDialog/PoolAssignmentDropdown';
import { DomainValidationStatus } from '../../types/DomainValidationStatus';
import { PoolAssignmentOption } from '../../types/PoolAssignmentOption';
import { PoolID } from '../../types/PoolID';
import { RoutingFilterPermission } from '../../types/RoutingFilterPermission';
import { getErrorMessageForDomainValidationStatus } from '../../utils/DomainUtilities';
import { getDistinctConflictMessages, hasSelectedConflictingProject } from '../../utils/RoutingFilterErrorUtilities';
import { ConflictError } from '../RoutingFiltersConflictContext';
import { RoutingFilterProjectSelector } from './RoutingFilterProjectSelector';

const permissionTypeHeaderId = 'routing-filter-permission-type-header';

export type RoutingFilterDetailsDialogBodyProps = Readonly<{
  onRoutingFilterPermissionChange: (permission: RoutingFilterPermission) => void;
  routingFilterPermission: RoutingFilterPermission;

  onDomainNameChange: (domainName: string) => void;
  domainName: string;
  domainValidationStatus: DomainValidationStatus;

  onProjectIdsChange: (projectIds: Set<string>) => void;
  projectIds: Set<string>;
  projects: ReadonlyArray<DestinationItem> | null;

  onPoolAssignmentChange: (poolID: PoolID | null) => void;
  assignedPoolID: PoolID | null;
  poolOptions: ReadonlyArray<PoolAssignmentOption>;

  errorMessage?: string;

  // Conflict/warning info carried in from the last save attempt when the dialog
  // is reopened on an unsaved (conflicting) filter. All optional so the create
  // flow and first-open edit render unchanged.
  conflictErrors?: ReadonlyArray<ConflictError>;
  conflictingProjectIds?: ReadonlySet<string>;
  showMissingDomainWideWarning?: boolean;
  hasDeletedProjects?: boolean;
  onClearDeletedProjects?: () => void;
  // True while an in-dialog "Clear Deleted Projects" edit is in flight, so the
  // button shows a spinner and blocks clicks — a second click can't fire a
  // duplicate request.
  isClearDeletedProjectsRequestInFlight?: boolean;
}>;

export function RoutingFilterDetailsDialogBody(props: RoutingFilterDetailsDialogBodyProps) {
  const isBlock = props.routingFilterPermission === RoutingFilterPermission.BLOCK;
  // Deduped on the stable error code, matching the table row's conflict popover
  // so the dialog and table read consistently.
  const conflictMessages = getDistinctConflictMessages(props.conflictErrors);
  // Once every conflicting project has been deselected there is nothing left
  // highlighted for the banner to explain, so hide it (a domain change alone
  // still surfaces on the next save attempt). Block rules have no projects, so
  // the conflict is always domain-level and the banner stays until re-save.
  const hasSelectedConflict = hasSelectedConflictingProject(props.conflictingProjectIds, props.projectIds);
  const showConflictBanner = conflictMessages.length > 0
    && (isBlock || props.conflictingProjectIds === undefined || props.conflictingProjectIds.size === 0 || hasSelectedConflict);

  const conflictBanner = (
    <Banner variant={'error'} testId={'routing-filter-dialog-conflict-banner'}>
      {conflictMessages.map(({ errorCode, message }) => <div key={errorCode}>{message}</div>)}
    </Banner>
  );

  return (
    <>
      {props.errorMessage && (
        <div role={'alert'} className={errorBannerClass} data-tb-test-id={'routing-filter-dialog-error-banner'}>
          {props.errorMessage}
        </div>
      )}
      <h3 id={permissionTypeHeaderId} className={permissionTypeHeaderClass}>
        {Messages.bridgeSiteSettingsRoutingFilterPermissionType()}
      </h3>
      <RoutingFilterPermissionsSelector
        routingFilterPermission={props.routingFilterPermission}
        onRoutingFilterPermissionChange={props.onRoutingFilterPermissionChange}
      />

      <h3 className={sectionHeaderClass}>{Messages.bridgeSiteSettingsRoutingFilterSectionHeader()}</h3>
      <RoutingFilterDomainTextField
        onDomainNameChange={props.onDomainNameChange}
        domainName={props.domainName}
        domainValidationStatus={props.domainValidationStatus}
      />
      {/* Block rules carry no projects, so a conflict is always domain-level:
          surface it directly beneath the Domain field. */}
      {isBlock && showConflictBanner && conflictBanner}
      {!isBlock && (
        <>
          <RoutingFilterProjectSelector
            projects={props.projects}
            projectIds={props.projectIds}
            onProjectIdsChange={props.onProjectIdsChange}
            conflictingProjectIds={props.conflictingProjectIds}
          />
          {/* Allow-rule conflicts are project-level: place the banner and the
              project-related warnings beneath the project selector. */}
          {showConflictBanner && conflictBanner}
          {props.hasDeletedProjects && (
            <Banner
              variant={'warning'}
              testId={'routing-filter-dialog-deleted-projects-banner'}
              button={props.onClearDeletedProjects ? (
                <ButtonWidget
                  buttonType={ButtonType.Outline}
                  onClick={props.onClearDeletedProjects}
                  showSpinner={props.isClearDeletedProjectsRequestInFlight}
                  testId={'routing-filter-dialog-clear-unspecified-projects-button'}
                >
                  {Messages.bridgeSiteSettingsRoutingFilterClearUnspecifiedProjects()}
                </ButtonWidget>
              ) : undefined}
            >
              {Messages.bridgeSiteSettingsRoutingFilterDeletedProjectsDescription()}
            </Banner>
          )}
          {props.showMissingDomainWideWarning && (
            <Banner variant={'warning'} testId={'routing-filter-dialog-missing-domain-wide-banner'}>
              {Messages.bridgeSiteSettingsRoutingFilterMissingDomainWideRuleDescription()}
            </Banner>
          )}
          <PoolAssignmentDropdown
            onPoolAssignmentChange={props.onPoolAssignmentChange}
            assignedPoolID={props.assignedPoolID}
            poolOptions={props.poolOptions}
          />
        </>
      )}
    </>
  );
}

// The dialog shows several banners that differ only in severity and content: an
// error-styled conflict banner and warning-styled deleted-projects and
// missing-domain-wide banners. InlineAlertWidget supplies the themed fill,
// border, status icon (dark-theme legible), and role="status" live region, so
// the call sites vary only by `variant`, their content, and an optional action
// button. Using it keeps these consistent with the rest of the app's alerts
// (the same widget the Default Pool banner uses) instead of hand-rolled styles.
type BannerProps = Readonly<{
  variant: 'error' | 'warning';
  testId: string;
  children: React.ReactNode;
  // Optional action rendered inside the banner (e.g. "Clear Deleted Projects").
  // InlineAlertWidget forces its buttonType to Outline and wraps it below the
  // text on narrow widths.
  button?: React.ReactElement;
}>;

function Banner(props: BannerProps) {
  const isError = props.variant === 'error';
  return (
    <InlineAlertWidget
      type={isError ? InlineAlertType.Error : InlineAlertType.Warning}
      testId={props.testId}
      button={props.button}
      // Mirror the severity onto a stable DOM attribute so headless tests can
      // assert error vs. warning directly on the rendered alert, rather than
      // relying only on the InlineAlertType `type` prop (which reaches the DOM
      // as a styling className, not a semantic hook).
      data-severity={props.variant}
      // InlineAlertWidget renders role="status" (a polite live region) for every
      // alert, which suits the advisory warning banners. An error banner blocks
      // the user's save, so it warrants an assertive announcement: an explicit
      // aria-live overrides the role's implicit politeness, so screen readers
      // interrupt for the error while warnings stay polite. (The widget hardcodes
      // role="status" and ignores a role prop, so aria-live is the escape hatch.)
      aria-live={isError ? 'assertive' : undefined}
      style={{ marginBottom: TabStyles.Spacing.DefaultSpace }}
    >
      <div className={bannerTextClass}>{props.children}</div>
    </InlineAlertWidget>
  );
}

type RoutingFilterPermissionsSelectorProps = Readonly<{
  onRoutingFilterPermissionChange: (permission: RoutingFilterPermission) => void;
  routingFilterPermission: RoutingFilterPermission;
}>;

function RoutingFilterPermissionsSelector(props: RoutingFilterPermissionsSelectorProps) {
  const permissionOptions: RadioButtonWithLabelOption<RoutingFilterPermission>[] = [
    {
      value: RoutingFilterPermission.ALLOW,
      testId: 'Allow',
      label: Messages.bridgeSiteSettingsAllow(),
    },
    {
      value: RoutingFilterPermission.BLOCK,
      testId: 'Block',
      label: Messages.bridgeSiteSettingsBlock(),
    },
  ];

  return (
    <RadioButtonWithLabelGroupWidget
      aria-labelledby={permissionTypeHeaderId}
      buttonList={permissionOptions}
      checkedIndex={props.routingFilterPermission === RoutingFilterPermission.ALLOW ? 0 : 1}
      handleChange={props.onRoutingFilterPermissionChange}
      layout={RadioLayoutType.Veritical}
    />
  );
}

type RoutingFilterDomainTextFieldProps = Readonly<{
  onDomainNameChange: (domainName: string) => void;
  domainName: string;
  domainValidationStatus: DomainValidationStatus;
}>;

function RoutingFilterDomainTextField(props: RoutingFilterDomainTextFieldProps) {
  const isValid = props.domainValidationStatus === DomainValidationStatus.NO_ERROR ? undefined : false;
  const errorMessage = getErrorMessageForDomainValidationStatus(props.domainValidationStatus);

  return (
    <TextFieldWidget
      label={Messages.bridgeSiteSettingsDomain()}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.onDomainNameChange(e.target.value)}
      onClear={() => props.onDomainNameChange('')}
      placeholder={''}
      testId={'routing-filter-domain-text-widget'}
      value={props.domainName}
      kind={'outline'}
      className={domainTextFieldClass}
      style={{ width: '100%' }}
      valid={isValid}
      message={errorMessage}
    />
  );
}

const errorBannerClass = cssClass('routing-filter-details-dialog-body', 'error-banner', {
  color: applyColor(DesignTokens.color.font.default.error, TabStyles.Colors.AttentionDark),
  padding: TabStyles.Spacing.DefaultSpace,
  marginBottom: TabStyles.Spacing.DefaultSpace,
  fontSize: TabStyles.Typography.Sizing.Default,
});

// Multiple conflict messages stack vertically inside the InlineAlertWidget body;
// space consecutive lines apart. The alert supplies fill/border/icon/color.
const bannerTextClass = cssClass('routing-filter-details-dialog-body', 'banner-text', {
  $nest: {
    '& > div + div': {
      marginTop: TabStyles.Spacing.SmallSpace,
    },
  },
});

const sectionHeaderClass = cssClass('routing-filter-details-dialog-body', 'section-header', {
  fontSize: TabStyles.Typography.Sizing.Default,
  fontWeight: 'bold',
  margin: 0,
  paddingTop: TabStyles.Spacing.MediumSpace,
  paddingBottom: TabStyles.Spacing.MediumSpace,
});

const permissionTypeHeaderClass = cssClass('routing-filter-details-dialog-body', 'permission-type-header', {
  fontSize: TabStyles.Typography.Sizing.Default,
  fontWeight: 'bold',
  margin: 0,
  paddingTop: 0,
  paddingBottom: TabStyles.Spacing.SmallSpace,
});

const domainTextFieldClass = cssClass('routing-filter-details-dialog-body', 'domain-text-field', {
  width: '100%',
  marginTop: TabStyles.Spacing.SmallSpace,
  marginBottom: TabStyles.Spacing.LargeSpace,
});
